import { QueryHookOptions, useQuery, gql } from '@apollo/client';

export interface AbstractCategory {
  id: string;
  slug: string;
  name: string;
}

type TL4Category$L2Category = {
  id: string;
  slug: string;
  name: string;
};

type TL4Category$L3Category = {
  id: string;
  slug: string;
  hasSpRegistration: boolean;
};

type TL4Category$RelatedService = {
  id: string;
  slug: string;
  name: string;
  imageUrl: string;
  requestsCount?: number;
};

type TL4Category$PopularService = {
  id: string;
  slug: string;
  name: string;
};

type TRequestUser = {
  id: string;
  name: string;
  avatar: string;
  gender: string;
};

export type TCategoryProvider = {
  id: string;
  slug: string;
  firstName: string;
  lastName: string;
  avatar: string;
  publicName: string;
  companyName: string;
  avatarUrl: string;
  rating: number;
  feedbacksCount: number;
  shortDescription: string;
  cityName: string;
  dateRegistered: string;
  featured: boolean;
  newcomer: boolean;
  yearsOfExperience: number;
  yearEstablished: number;
};

type TCategoryRequest = {
  id: string;
  user: TRequestUser;
  city?: {
    name: string;
    regionName: string;
  };
  l4Category?: {
    name: string;
    slug: string;
  };
  providers: TCategoryProvider[];
  details: {
    answer: string;
    answer_id: string;
    question: string;
    question_id: string;
  }[];
};

type TCategoryFeedback = {
  id: string;
  comment: string;
  value: number;
  createdAt: string;
  user: {
    id: string;
    name: string;
    avatar: string;
    city: string;
    gender?: string;
  };
  provider: {
    id: string;
    slug: string;
    avatar: string;
    name: string;
    city: string;
  };
  category?: {
    name: string;
    slug: string;
  };
};

export type TL4Category = {
  id: string;
  slug: string;
  name: string;
  siteTitle: string;
  siteTitleTemplate: string;
  siteTitleCityTemplate: string;
  coverUrl: string;
  mobileCoverUrl: string;
  providerCount: number;
  avgResponseTime: string;
  description: string;
  seoDescription: string;
  questionsAndAnswers: string;
  isNew: boolean;
  companyListingSlug?: string;
  medianPrice?: number;
  lowerQuartilePrice?: number;
  feedbacksInfo: {
    averageValue: number;
    count: number;
  };
  l2: TL4Category$L2Category;
  l3: TL4Category$L3Category;
  relatedServices: TL4Category$RelatedService[];
  popularServices: TL4Category$PopularService[];
  requests: TCategoryRequest[];
  feedbacks: TCategoryFeedback[];
  providers: TCategoryProvider[];
};

type L4BySlugQueryResult = {
  l4BySlug: TL4Category;
};

type L4BySlugQueryVariables = {
  slug: string;
  city?: string;
  showHiddenCategories?: boolean;
};

const l4BySlugQuery = gql`
  query l4BySlug($slug: String!, $city: String, $showHiddenCategories: Boolean) {
    l4BySlug(slug: $slug, city: $city, showHiddenCategories: $showHiddenCategories) {
      id
      slug
      name
      siteTitle
      siteTitleTemplate
      siteTitleCityTemplate
      coverUrl
      mobileCoverUrl
      providerCount
      avgResponseTime
      description
      seoDescription
      questionsAndAnswers
      isNew
      companyListingSlug
      medianPrice
      lowerQuartilePrice
      feedbacksInfo {
        averageValue
        count
      }
      l2 {
        id
        slug
        name
      }
      l3 {
        id
        slug
        hasSpRegistration
      }
      relatedServices {
        id
        slug
        name
        imageUrl
      }
      popularServices {
        id
        slug
        name
      }
      requests {
        id
        user {
          id
          name
          gender
          avatar
        }
        providers {
          id
          avatar
        }
        details {
          question_id
          question
          answer_id
          answer
        }
      }
      feedbacks {
        id
        value
        comment
        createdAt
        user {
          id
          name
          avatar
          city
        }
        provider {
          id
          name
          slug
          avatar
          city
        }
      }
      providers(limit: 50) {
        id
        slug
        firstName
        lastName
        publicName
        avatarUrl
        companyName
        rating
        feedbacksCount
        shortDescription
        cityName
        dateRegistered
        featured
        yearsOfExperience
        yearEstablished
      }
    }
  }
`;

export const useL4BySlugQuery = (options?: QueryHookOptions<L4BySlugQueryVariables>) => {
  return useQuery(l4BySlugQuery, options);
};
